import { isTokenExpired, getTimeUntilExpiration } from './tokenUtils';
import activityDetector from './activityDetector';
import authStorage from './authStorage';

class TokenExpirationManager {
  constructor() {
    this.intervalId = null;
    this.timeoutId = null;
    this.onExpired = null;
    this.onRefresh = null;
    this.checkInterval = 5 * 60 * 1000; // Check every 5 minutes (less aggressive)
    this.refreshThreshold = 30 * 60 * 1000; // Refresh 30 minutes before expiry
    this.activityThreshold = 15 * 60 * 1000; // Consider user inactive after 15 minutes
    this.isRefreshing = false; // Prevent multiple simultaneous refresh attempts
  }

  // Start monitoring token expiration with refresh callback
  startMonitoring(onExpiredCallback, onRefreshCallback = null) {
    this.onExpired = onExpiredCallback;
    this.onRefresh = onRefreshCallback;
    
    // Clear any existing intervals
    this.stopMonitoring();
    
    // Start activity monitoring
    activityDetector.startMonitoring();
    
    // Set up activity-based token check
    activityDetector.setActivityCallback(() => {
      // Check token when user becomes active (in case they were inactive for a while)
      this.checkTokenExpiration();
    });
    
    // Start periodic checking (less frequent now)
    this.intervalId = setInterval(() => {
      this.checkTokenExpiration();
    }, this.checkInterval);
    
    // Set up precise timeout for current token
    this.setupPreciseTimeout();
  }

  // Stop monitoring
  stopMonitoring() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    
    // Stop activity monitoring
    activityDetector.stopMonitoring();
  }

  // Check if current token is expired or needs refresh
  checkTokenExpiration() {
    const token = authStorage.getToken();
    
    if (!token) {
      return;
    }
    
    const timeUntilExpiry = getTimeUntilExpiration(token);
    
    if (timeUntilExpiry <= 0) {
      // Token is already expired
      console.log('🚨 Token expired, triggering logout');
      this.triggerExpiredCallback();
      return;
    }
    
    // Check if token needs proactive refresh
    if (timeUntilExpiry <= this.refreshThreshold) {
      // Only refresh if user has been active recently
      const isActive = activityDetector.isUserActive(this.activityThreshold);
      
      if (isActive && !this.isRefreshing) {
        console.log('🔄 Token expiring soon and user is active, attempting proactive refresh');
        this.attemptProactiveRefresh();
      } else if (!isActive) {
        console.log('🔍 Token expiring soon but user inactive, will logout on next activity');
        // Don't refresh for inactive users, but don't logout either until they're active
      }
    }
  }

  // Set up precise timeout for current token
  setupPreciseTimeout() {
    const token = authStorage.getToken();
    if (!token) return;
    
    const timeUntilExpiration = getTimeUntilExpiration(token);
    if (!timeUntilExpiration || timeUntilExpiration <= 0) {
      // Token already expired
      this.triggerExpiredCallback();
      return;
    }
    
    // Set timeout for exact expiration time (with 1 second buffer)
    const timeoutDelay = Math.max(timeUntilExpiration - 1000, 1000);
    
    this.timeoutId = setTimeout(() => {
      this.triggerExpiredCallback();
    }, timeoutDelay);
  }

  // Attempt proactive token refresh
  async attemptProactiveRefresh() {
    if (this.isRefreshing) {
      console.log('🔄 Refresh already in progress, skipping');
      return;
    }

    this.isRefreshing = true;
    
    try {
      if (this.onRefresh) {
        console.log('🔄 Attempting proactive token refresh');
        await this.onRefresh();
        console.log('✅ Proactive token refresh successful');
        
        // Reset the timeout for the new token
        this.setupPreciseTimeout();
      }
    } catch (error) {
      console.error('❌ Proactive token refresh failed:', error);
      // If proactive refresh fails, fall back to logout
      this.triggerExpiredCallback();
    } finally {
      this.isRefreshing = false;
    }
  }

  // Trigger the expired callback
  triggerExpiredCallback() {
    if (this.onExpired) {
      this.onExpired();
    }
  }

  // Update monitoring when token changes
  updateToken() {
    if (this.intervalId) {
      // Restart monitoring with new token
      this.setupPreciseTimeout();
    }
  }
}

// Export singleton instance
export default new TokenExpirationManager();
